import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  buildMdDocumentModel,
  indexMarkdownSearchMatches,
  type MdDocumentSection,
  type MdSearchMatch,
} from './mdDocumentModel'

const MIN_SEARCH_LENGTH = 2
const SEARCH_DEBOUNCE_MS = 300

export function useMdTextSearchIndex({
  searchValue,
  isEditing,
  content,
  sections: providedSections,
}: {
  searchValue: string
  isEditing: boolean
  content: string
  sections?: MdDocumentSection[]
}) {
  const sections = useMemo(
    () => providedSections ?? buildMdDocumentModel(content).sections,
    [content, providedSections]
  )

  const trimmedSearch = searchValue.trim()
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('')

  useEffect(() => {
    if (trimmedSearch.length < MIN_SEARCH_LENGTH) {
      setDebouncedSearchValue('')
      return
    }
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearchValue(trimmedSearch)
    }, SEARCH_DEBOUNCE_MS)
    return () => window.clearTimeout(timeoutId)
  }, [trimmedSearch])

  const [matches, setMatches] = useState<MdSearchMatch[]>([])
  const [isSearchPending, setIsSearchPending] = useState(false)

  useEffect(() => {
    if (isEditing || debouncedSearchValue === '') {
      setIsSearchPending(false)
      setMatches((current) => (current.length === 0 ? current : []))
      return
    }

    let cancelled = false
    setIsSearchPending(true)
    const query = debouncedSearchValue

    const timeoutId = window.setTimeout(() => {
      const nextMatches = indexMarkdownSearchMatches(content, sections, query)
      if (!cancelled) {
        setMatches(nextMatches)
        setIsSearchPending(false)
      }
    }, 0)

    return () => {
      cancelled = true
      window.clearTimeout(timeoutId)
    }
  }, [content, debouncedSearchValue, isEditing, sections])

  const [activeMatchIndex, setActiveMatchIndex] = useState(0)

  useEffect(() => {
    setActiveMatchIndex(0)
  }, [matches])

  const hasSearchTerm = debouncedSearchValue.length >= MIN_SEARCH_LENGTH
  const matchCount = matches.length
  const activeMatch = matches[activeMatchIndex]

  const goToPreviousMatch = useCallback(() => {
    if (matchCount === 0) {
      return
    }
    setActiveMatchIndex((currentIndex) => (currentIndex - 1 + matchCount) % matchCount)
  }, [matchCount])

  const goToNextMatch = useCallback(() => {
    if (matchCount === 0) {
      return
    }
    setActiveMatchIndex((currentIndex) => (currentIndex + 1) % matchCount)
  }, [matchCount])

  return {
    matches,
    activeMatch,
    hasSearchTerm,
    isSearchPending,
    matchCount,
    activeMatchIndex,
    goToPreviousMatch,
    goToNextMatch,
  }
}
